import React from 'react'
import type { ApolloClient } from 'apollo-client'
import { ParentAckLoader } from './ParentAckLoader'
import { AppearanceHistoryStore } from './AppearanceHistoryStore'
import { assembleBatch } from './ParentFanoutAssembler'
import { getBatchTargetRange } from './batchTimeRange'
import type { BoardMeta, ParentFanoutRecord, TimeRange } from './types'

export const PARENT_PAGE_SIZE = 10
export const CHILD_BOARD_PAGE_SIZE = 50
export const MATCH_WINDOW_MS = 5000
export const TIME_RANGE_BUFFER_MS = 1000

const updateRecords = (
  current: ParentFanoutRecord[],
  updates: ParentFanoutRecord[]
): ParentFanoutRecord[] => {
  const byParentId = new Map(current.map(record => [record.parent.id, record]))
  for (const update of updates) {
    byParentId.set(update.parent.id, update)
  }
  return Array.from(byParentId.values()).sort((a, b) => b.parent.created - a.parent.created)
}

const fetchNextNonEmptyBatch = async (store: AppearanceHistoryStore, maxPages = 3) => {
  let previousCursor = store.getNextCursor()
  for (let i = 0; i < maxPages; i += 1) {
    const result = await store.fetchNextPage()
    const cursorDidNotAdvance = result.nextCursor === previousCursor
    if (result.rows.length > 0 || result.nextCursor == null || cursorDidNotAdvance) {
      return result
    }
    previousCursor = result.nextCursor
  }

  return {
    rows: [] as ParentFanoutRecord['parent'][],
    nextCursor: store.getNextCursor()
  }
}

export const useMessageAppearanceHistory = ({
  client,
  arrayBoardId,
  childBoards
}: {
  client: ApolloClient<object>
  arrayBoardId: string
  childBoards: BoardMeta[]
}) => {
  const [records, setRecords] = React.useState<ParentFanoutRecord[]>([])
  const [nextCursor, setNextCursor] = React.useState<string | null>(null)
  const [loadingInitial, setLoadingInitial] = React.useState(false)
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [assembling, setAssembling] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const arrayStoreRef = React.useRef<AppearanceHistoryStore | null>(null)
  const childBoardsRef = React.useRef<BoardMeta[]>(childBoards)
  const childStoresRef = React.useRef<Record<string, AppearanceHistoryStore>>({})
  const ackLoaderRef = React.useRef<ParentAckLoader | null>(null)
  const activeRangesRef = React.useRef<TimeRange[]>([])
  const processGenerationRef = React.useRef(0)

  React.useEffect(() => {
    childBoardsRef.current = childBoards
  }, [childBoards])

  const ensureStores = React.useCallback(() => {
    if (!arrayStoreRef.current) {
      arrayStoreRef.current = new AppearanceHistoryStore({
        client,
        boardId: arrayBoardId,
        pageSize: PARENT_PAGE_SIZE
      })
    }
    if (!ackLoaderRef.current) {
      ackLoaderRef.current = new ParentAckLoader(client)
    }

    const nextChildStores: Record<string, AppearanceHistoryStore> = {}
    for (const board of childBoardsRef.current) {
      nextChildStores[board.id] =
        childStoresRef.current[board.id] ||
        new AppearanceHistoryStore({
          client,
          boardId: board.id,
          pageSize: CHILD_BOARD_PAGE_SIZE
        })
    }
    childStoresRef.current = nextChildStores
  }, [arrayBoardId, client])

  const processBatch = React.useCallback(
    async (parents: ParentFanoutRecord['parent'][]) => {
      if (parents.length === 0) return
      const generation = ++processGenerationRef.current
      const isCurrent = () => processGenerationRef.current === generation
      ensureStores()
      const boards = childBoardsRef.current

      const pending = parents.map(parent => ({
        parent,
        rows: boards.map(board => ({
          board,
          matchedAppearance: null,
          coverageStatus: 'unknown' as const,
          noAppearanceReason: null
        })),
        matchStatus: 'pending' as const,
        ackStatus: 'idle' as const,
        acksByAppearanceId: new Map<string, number>()
      }))
      if (isCurrent()) {
        setRecords(prev => updateRecords(prev, pending))
      }

      try {
        if (boards.length === 0) {
          const readyWithoutBoards = pending.map(record => ({
            ...record,
            matchStatus: 'ready' as const
          }))
          if (isCurrent()) {
            setRecords(prev => updateRecords(prev, readyWithoutBoards))
          }
          return
        }

        const targetRange = getBatchTargetRange(parents, MATCH_WINDOW_MS, TIME_RANGE_BUFFER_MS)
        if (!targetRange) return

        if (isCurrent()) {
          setAssembling(true)
        }
        const assembled = await assembleBatch({
          parents,
          childBoards: boards,
          childStores: childStoresRef.current,
          targetRange,
          matchWindowMs: MATCH_WINDOW_MS
        })
        if (!isCurrent()) return
        setRecords(prev => updateRecords(prev, assembled))

        activeRangesRef.current = [...activeRangesRef.current, targetRange]
        const mergedActiveRanges = [...activeRangesRef.current]
        for (const store of Object.values(childStoresRef.current)) {
          store.trimToRanges(mergedActiveRanges, TIME_RANGE_BUFFER_MS)
        }
        const reassembledAfterTrim = await assembleBatch({
          parents,
          childBoards: boards,
          childStores: childStoresRef.current,
          targetRange,
          matchWindowMs: MATCH_WINDOW_MS
        })
        if (!isCurrent()) return
        setRecords(prev => updateRecords(prev, reassembledAfterTrim))

        const ackLoader = ackLoaderRef.current
        if (!ackLoader) return
        const withLoadingAcks = reassembledAfterTrim.map(record => ({ ...record, ackStatus: 'loading' as const }))
        setRecords(prev => updateRecords(prev, withLoadingAcks))

        const withAcks = await Promise.all(
          reassembledAfterTrim.map(async record => {
            const appearanceIds = record.rows
              .map(row => row.matchedAppearance?.id)
              .filter((value): value is string => !!value)
            try {
              const cache = await ackLoader.ensureParentAcks(record.parent.id, appearanceIds)
              const acksByAppearanceId = new Map<string, number>()
              cache.forEach((timestamp, appearanceId) => {
                if (timestamp != null) {
                  acksByAppearanceId.set(appearanceId, timestamp)
                }
              })
              return {
                ...record,
                ackStatus: 'ready' as const,
                acksByAppearanceId
              }
            } catch {
              return {
                ...record,
                ackStatus: 'error' as const
              }
            }
          })
        )
        if (!isCurrent()) return
        setRecords(prev => updateRecords(prev, withAcks))
      } catch {
        if (isCurrent()) {
          setError('Unable to process parent appearance batch.')
        }
      } finally {
        if (isCurrent()) {
          setAssembling(false)
        }
      }
    },
    [ensureStores]
  )

  const loadMore = React.useCallback(async () => {
    if (!arrayStoreRef.current || loadingMore || nextCursor == null) return

    setLoadingMore(true)
    setError(null)
    try {
      const result = await fetchNextNonEmptyBatch(arrayStoreRef.current)
      setNextCursor(result.nextCursor)
      await processBatch(result.rows)
    } catch {
      setError('Unable to load more parent message appearances.')
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, nextCursor, processBatch])

  React.useEffect(() => {
    if (childBoards.length === 0 || records.length === 0) return
    const hasUnhydratedRows = records.some(record => record.rows.length === 0)
    if (!hasUnhydratedRows) return

    const parents = records.map(record => record.parent)
    void processBatch(parents)
  }, [childBoards, processBatch, records])

  React.useEffect(() => {
    if (!arrayBoardId) return

    let cancelled = false
    const run = async () => {
      ensureStores()
      setRecords([])
      setError(null)
      activeRangesRef.current = []
      ackLoaderRef.current?.reset()
      setLoadingInitial(true)
      setAssembling(false)
      try {
        const result = await fetchNextNonEmptyBatch(arrayStoreRef.current!)
        if (cancelled) return
        setNextCursor(result.nextCursor)
        await processBatch(result.rows)
      } catch {
        if (!cancelled) {
          setError('Unable to load parent message appearances.')
        }
      } finally {
        if (!cancelled) {
          setLoadingInitial(false)
        }
      }
    }

    arrayStoreRef.current = null
    childStoresRef.current = {}
    run()
    return () => {
      cancelled = true
      processGenerationRef.current += 1
    }
  }, [arrayBoardId, ensureStores, processBatch])

  return {
    records,
    nextCursor,
    loadingInitial,
    loadingMore,
    assembling,
    error,
    loadMore
  }
}
